import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { canModifyLead, getCrossTabStorageKeys, hasLeadAccess } from '../../utils/permissions';
import { Button } from '../common';

const EditLeadModal = ({ isOpen, onClose, lead }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    name: '', // Keep for backward compatibility
    email: '',
    company: '',
    role: '', // Added role field
    secondary_email: '',
    phone: '',
    secondary_phone: '',
    original_source: '',
    latest_source: '',
    status: '',
    country: '',
    assigned_to: '',
    call_attempts: 0,
    subscribed: true,
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [users, setUsers] = useState([]);

  // Handle ESC key press
  const handleEscapeKey = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);
  
  // Add event listener for ESC key
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }
    
    // Clean up
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, handleEscapeKey]);

  // Init form data with current lead info when lead or isOpen changes
  useEffect(() => {
    if (lead && isOpen) {
      setFormData({
        first_name: lead.first_name || '',
        last_name: lead.last_name || '',
        name: lead.name || '',
        email: lead.email || '',
        company: lead.company || '',
        role: lead.role || '', // Added role field
        secondary_email: lead.secondary_email || '',
        phone: lead.phone || '',
        secondary_phone: lead.secondary_phone || '',
        original_source: lead.original_source || lead.source || '',
        latest_source: lead.latest_source || lead.source || '',
        status: lead.status || 'new',
        country: lead.country || '',
        assigned_to: lead.assigned_to ? lead.assigned_to.toString() : '',
        call_attempts: lead.call_attempts || 0,
        subscribed: lead.subscribed !== undefined ? lead.subscribed : true,
        address: lead.address || ''
      });
    }
  }, [lead, isOpen]);

  // Fetch users for assignment dropdown
  useEffect(() => {
    if (!isOpen) return;

    const fetchUsers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        const res = await axios.get('/api/auth/users?has_lead_access=true', config);

        // Make all users available for lead assignment
        const filteredUsers = res.data;

        setUsers(filteredUsers);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users');
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user has permission to update this lead
    if (!canModifyLead('update', lead)) {
      setError('You do not have permission to update this lead');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      setSuccess(false);
      
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Convert values to appropriate types
      const updatedLead = {
        ...formData,
        call_attempts: parseInt(formData.call_attempts, 10),
        assigned_to: formData.assigned_to || null
      };
      
      const res = await axios.put(
        `/api/leads/${lead.id}`,
        updatedLead,
        config
      );
      
      setSuccess(true);
      setSaving(false);
      
      // Notify other tabs about the lead update
      const storageKeys = getCrossTabStorageKeys();
      localStorage.setItem(storageKeys.LEAD_UPDATED, JSON.stringify({
        id: lead.id,
        timestamp: new Date().getTime()
      }));
      
      // Close modal and refresh data after successful update
      setTimeout(() => {
        onClose(true); // Pass true to indicate successful update
      }, 1500);
      
    } catch (err) {
      console.error('Error updating lead:', err);
      setError(err.response?.data?.message || 'Error updating lead');
      setSaving(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 overflow-y-auto z-50" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>
        
        <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Lead</h3>
              <button 
                onClick={onClose}
                className="bg-white rounded-md p-1 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            {loading ? (
              <div className="flex justify-center items-center py-4">
                <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && (
                  <div className="mb-4 p-2 text-sm text-red-700 bg-red-100 rounded-md">
                    {error}
                  </div>
                )}
                
                {success && (
                  <div className="mb-4 p-2 text-sm text-green-700 bg-green-100 rounded-md flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Lead updated successfully!
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={(e) => {
                        // Update first_name and also update full name for backward compatibility
                        const newFirstName = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          first_name: newFirstName,
                          name: `${newFirstName} ${formData.last_name}`.trim()
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={(e) => {
                        // Update last_name and also update full name for backward compatibility
                        const newLastName = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          last_name: newLastName,
                          name: `${formData.first_name} ${newLastName}`.trim()
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <input
                      type="text"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="E.g. CEO, Marketing Manager, etc."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Email</label>
                    <input
                      type="email"
                      name="secondary_email"
                      value={formData.secondary_email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Phone</label>
                    <input
                      type="tel"
                      name="secondary_phone"
                      value={formData.secondary_phone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Secondary phone number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Address Field */}
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Street address, city, state, zip code"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Original Source</label>
                    <input
                      type="text"
                      name="original_source"
                      value={formData.original_source}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      readOnly
                    />
                    <p className="mt-0.5 text-xs text-gray-500">Original source cannot be changed</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Latest Source</label>
                    <input
                      type="text"
                      name="latest_source"
                      value={formData.latest_source}
                      onChange={(e) => {
                        // When latest_source changes, also update the legacy source field for backward compatibility
                        const newValue = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          latest_source: newValue,
                          source: newValue
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="new">New</option>
                      <option value="connected">Connected</option>
                      <option value="HOT">HOT</option>
                      <option value="disqualified">Disqualified</option>
                      <option value="converted">Converted</option>
                      <option value="unreachable">Unreachable</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                    <select
                      name="assigned_to"
                      value={formData.assigned_to}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Unassigned</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Call Attempts</label>
                    <input
                      type="number"
                      name="call_attempts"
                      value={formData.call_attempts}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                      max="10"
                    />
                  </div>
                </div>
                
                <div className="mb-4 flex items-center">
                  <input
                    type="checkbox"
                    id="subscribed"
                    name="subscribed"
                    checked={formData.subscribed}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="subscribed" className="ml-2 block text-sm text-gray-700">
                    Subscribed to emails
                  </label>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <Button
                    type="button"
                    onClick={onClose}
                    variant="outline"
                    className="px-4 py-2 text-sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    className="px-4 py-2 text-sm"
                    disabled={saving}
                    isLoading={saving}
                  >
                    {saving ? (
                      'Saving...'
                    ) : 'Save Changes'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditLeadModal;
